import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Star, Trash2, Plus, X, Pencil, Check } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import PlaceAutocomplete from "@/components/PlaceAutocomplete";

export default function AddressAliasesDialog({ open, onOpenChange, onAliasesChanged }) {
  const [aliases, setAliases] = useState([]);
  const [loading, setLoading] = useState(false);
  const [newName, setNewName] = useState("");
  const [newAddress, setNewAddress] = useState("");
  const [newLat, setNewLat] = useState(null);
  const [newLng, setNewLng] = useState(null);
  const [newPlaceId, setNewPlaceId] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({ name: "", address: "", lat: null, lng: null, place_id: null });

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/aliases");
      setAliases(Array.isArray(data) ? data : []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) load();
  }, [open]);

  const create = async () => {
    if (!newName.trim() || !newAddress.trim()) {
      toast.error("Nombre y dirección son obligatorios");
      return;
    }
    try {
      await api.post("/aliases", {
        name: newName.trim(),
        address: newAddress.trim(),
        lat: newLat,
        lng: newLng,
        place_id: newPlaceId,
      });
      toast.success(`"${newName}" guardado`);
      setNewName(""); setNewAddress(""); setNewLat(null); setNewLng(null); setNewPlaceId(null);
      await load();
      onAliasesChanged?.();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const startEdit = (a) => {
    setEditingId(a.id);
    setEditForm({ name: a.name, address: a.address, lat: a.lat, lng: a.lng, place_id: a.place_id || null });
  };

  const saveEdit = async () => {
    if (!editingId) return;
    try {
      await api.patch(`/aliases/${editingId}`, editForm);
      toast.success("Alias actualizado");
      setEditingId(null);
      await load();
      onAliasesChanged?.();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const remove = async (a) => {
    if (!window.confirm(`¿Eliminar "${a.name}"?`)) return;
    try {
      await api.delete(`/aliases/${a.id}`);
      toast.success("Eliminado");
      await load();
      onAliasesChanged?.();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-card border-border max-w-2xl" data-testid="aliases-dialog">
        <DialogHeader>
          <DialogTitle style={{ fontFamily: "Manrope" }} className="text-2xl flex items-center gap-2">
            <Star className="w-5 h-5 text-primary" /> Direcciones guardadas
          </DialogTitle>
        </DialogHeader>

        {/* Create new */}
        <div className="bg-secondary/40 border border-border rounded-md p-3 space-y-2">
          <div className="text-xs font-mono uppercase tracking-widest text-muted-foreground">
            <Plus className="w-3 h-3 inline mr-1" /> Agregar nueva
          </div>
          <div className="grid grid-cols-3 gap-2">
            <div className="col-span-1 space-y-1">
              <Label className="text-xs">Nombre corto</Label>
              <Input
                data-testid="alias-new-name"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                placeholder="Ej: La Mexicana"
                className="bg-input border-border"
              />
            </div>
            <div className="col-span-2 space-y-1">
              <Label className="text-xs">Dirección real</Label>
              <PlaceAutocomplete
                value={newAddress}
                onChange={setNewAddress}
                onPlace={({ address, lat, lng, place_id }) => {
                  setNewAddress(address);
                  setNewLat(lat);
                  setNewLng(lng);
                  setNewPlaceId(place_id || null);
                }}
                testId="alias-new-address"
                placeholder="Ej: Maxx Foods, 4055 23rd Ave SW, Naples FL"
                className="bg-input border-border"
              />
            </div>
          </div>
          <div className="flex justify-end">
            <Button
              onClick={create}
              size="sm"
              className="bg-primary text-primary-foreground hover:bg-primary/90 font-bold"
              data-testid="alias-new-submit"
            >
              <Plus className="w-4 h-4 mr-1" /> Guardar
            </Button>
          </div>
        </div>

        {/* Existing — filtered live by what is typed in "Nombre corto" so the
            dispatcher sees at once if the alias already exists. */}
        {(() => {
          const term = newName.trim().toLowerCase();
          const visible = term
            ? aliases.filter((a) => (a.name || "").toLowerCase().includes(term) || (a.address || "").toLowerCase().includes(term))
            : aliases;
          const exact = term && aliases.find((a) => (a.name || "").trim().toLowerCase() === term);
          return (
            <>
              {term && (
                <div
                  className={`flex items-center justify-between text-[11px] font-mono px-1 ${exact ? "text-red-300" : "text-muted-foreground"}`}
                  data-testid="aliases-filter-status"
                >
                  <span>
                    {exact
                      ? <>⚠ Ya existe una dirección llamada <b>"{exact.name}"</b></>
                      : visible.length
                        ? <>{visible.length} coincidencia{visible.length === 1 ? "" : "s"} con "{newName.trim()}"</>
                        : <>Sin coincidencias — "{newName.trim()}" es un nombre nuevo</>}
                  </span>
                  <button type="button" onClick={() => setNewName("")} className="text-muted-foreground hover:text-white" data-testid="aliases-filter-clear">limpiar</button>
                </div>
              )}
        <div className="max-h-80 overflow-y-auto space-y-1 pr-1" data-testid="aliases-list">
          {loading && <div className="text-xs text-muted-foreground py-2 text-center">Cargando…</div>}
          {!loading && aliases.length === 0 && (
            <div className="text-xs text-muted-foreground py-4 text-center">
              Sin direcciones guardadas.
            </div>
          )}
          {visible.map((a) => {
            const isEditing = editingId === a.id;
            if (isEditing) {
              return (
                <div key={a.id} className="grid grid-cols-3 gap-2 items-start p-2 rounded border border-primary/40 bg-primary/5">
                  <Input
                    value={editForm.name}
                    onChange={(e) => setEditForm((f) => ({ ...f, name: e.target.value }))}
                    className="bg-input border-border text-xs"
                    data-testid={`alias-edit-name-${a.id}`}
                  />
                  <div className="col-span-2 flex gap-1">
                    <div className="flex-1">
                      <PlaceAutocomplete
                        value={editForm.address}
                        onChange={(v) => setEditForm((f) => ({ ...f, address: v }))}
                        onPlace={({ address, lat, lng, place_id }) =>
                          setEditForm((f) => ({ ...f, address, lat, lng, place_id: place_id || null }))
                        }
                        testId={`alias-edit-address-${a.id}`}
                        className="bg-input border-border"
                      />
                    </div>
                    <Button size="icon" variant="ghost" onClick={saveEdit} data-testid={`alias-save-${a.id}`}>
                      <Check className="w-4 h-4 text-emerald-400" />
                    </Button>
                    <Button size="icon" variant="ghost" onClick={() => setEditingId(null)}>
                      <X className="w-4 h-4 text-muted-foreground" />
                    </Button>
                  </div>
                </div>
              );
            }
            return (
              <div
                key={a.id}
                data-testid={`alias-row-${a.id}`}
                className={`grid grid-cols-[130px_1fr_auto] gap-3 items-center p-2 rounded border transition-colors ${
                  newName.trim() && (a.name || "").trim().toLowerCase() === newName.trim().toLowerCase()
                    ? "border-red-500/60 bg-red-500/10"
                    : "border-border/50 hover:border-border hover:bg-secondary/30"
                }`}
              >
                <div className="font-bold text-white text-sm truncate flex items-center gap-1.5">
                  <Star className="w-3 h-3 text-primary flex-shrink-0" />
                  {a.name}
                </div>
                <div className="text-xs text-muted-foreground truncate">{a.address}</div>
                <div className="flex gap-0.5">
                  <Button size="icon" variant="ghost" onClick={() => startEdit(a)} data-testid={`alias-edit-${a.id}`} title="Editar">
                    <Pencil className="w-3.5 h-3.5" />
                  </Button>
                  <Button size="icon" variant="ghost" onClick={() => remove(a)} data-testid={`alias-delete-${a.id}`} title="Eliminar">
                    <Trash2 className="w-3.5 h-3.5 text-red-400" />
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
            </>
          );
        })()}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="aliases-close">Cerrar</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
